import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Typography,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  // CalendarToday as CalendarIcon, // Oculto
  // Schedule as ScheduleIcon, // Oculto
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Worker } from '../types';
import { workerService } from '../services/workerService';
import { useAuth } from '../contexts/AuthContext';

interface WorkersTableProps {
  onEditWorker: (worker: Worker) => void;
  onViewCalendar: (worker: Worker) => void;
  onViewScheduleManager: (worker: Worker) => void;
  onCreateWorker: () => void;
  refreshTrigger?: number; // Para forzar actualización
}

export const WorkersTable: React.FC<WorkersTableProps> = ({
  onEditWorker,
  onViewCalendar,
  onViewScheduleManager,
  onCreateWorker,
  refreshTrigger,
}) => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    loadWorkers();
  }, []);

  // Recargar trabajadores cuando cambie el refreshTrigger
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadWorkers();
    }
  }, [refreshTrigger]);

  const loadWorkers = async () => {
    try {
      setLoading(true);
      const data = await workerService.getWorkers();
      setWorkers(data);
    } catch (error) {
      console.error('Error cargando trabajadores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorker = async (workerId: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este trabajador?')) {
      try {
        await workerService.deleteWorker(workerId);
        await loadWorkers();
      } catch (error) {
        console.error('Error eliminando trabajador:', error);
      }
    }
  };

  const filteredWorkers = workers.filter(worker =>
    worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Cargando trabajadores...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Gestión de Trabajadores
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Administra y gestiona todos los trabajadores de tu empresa
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateWorker}
          sx={{
            background: 'linear-gradient(135deg, #10b981, #059669)',
            px: 3,
            py: 1.5,
            fontSize: '0.9rem',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #059669, #047857)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          + Crear Nuevo Trabajador
        </Button>
      </Box>

      {/* Search Section (match CompaniesPage) */}
      <Box sx={{ mb: 3, maxWidth: 500 }}>
        <TextField
          placeholder="Buscar trabajador por nombre o empresa"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                <span style={{
                  width: 16,
                  height: 16,
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #5ac8fa 0%, #a855f7 100%)',
                  WebkitMask: 'url("data:image/svg+xml;utf8,<?xml version=\'1.0\' encoding=\'UTF-8\'?><svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'><path fill=\'%23000\' d=\'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z\'/></svg>") center / contain no-repeat',
                  mask: 'url("data:image/svg+xml;utf8,<?xml version=\'1.0\' encoding=\'UTF-8\'?><svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'><path fill=\'%23000\' d=\'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z\'/></svg>") center / contain no-repeat'
                }} />
              </Box>
            ),
            sx: { borderRadius: 5, backgroundColor: 'white', pl: 0.5 }
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>PIN</strong></TableCell>
              <TableCell><strong>Empresa</strong></TableCell>
              <TableCell><strong>Precio / Overtime</strong></TableCell>
              <TableCell><strong>Rol</strong></TableCell>
              <TableCell><strong>Acción</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWorkers.map((worker) => (
              <TableRow key={worker.id} hover>
                <TableCell>{worker.worker_id || worker.id}</TableCell>
                <TableCell>{worker.name}</TableCell>
                <TableCell>{worker.pin || '-'}</TableCell>
                <TableCell>{worker.company_name}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatCurrency(worker.price_per_hour)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatCurrency(worker.overtime_price)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={worker.role}
                    color={worker.role === 'Jefe de empresa' ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => onEditWorker(worker)}
                      color="primary"
                      title="Editar trabajador"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onViewCalendar(worker)}
                      color={worker.role === 'Jefe de empresa' ? 'default' : 'success'}
                      title="Ver perfil"
                    >
                      <ViewIcon />
                    </IconButton>
                    {/* Iconos de calendario y reloj ocultos */}
                    {/* <IconButton
                      size="small"
                      onClick={() => onViewCalendar(worker)}
                      color="info"
                      title="Ver calendario"
                    >
                      <CalendarIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onViewScheduleManager(worker)}
                      color="secondary"
                      title="Gestionar horarios"
                    >
                      <ScheduleIcon />
                    </IconButton> */}
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteWorker(worker.id)}
                      color="error"
                      title="Eliminar trabajador"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
